/**
 * Spec 087 — Coverage Dashboard
 *
 * Renders the three-score triptych (Coverage / Quality / Topic Distribution)
 * for the currently-selected brand, plus an analysis history and a lazy-
 * loaded cluster map.
 *
 * Phase 9 Session B additions:
 *   - gap 087-1: "Start analysis" CTA opens a dialog that calls
 *     POST /api/v1/brands/:brandId/sitemap-analyses with cost preview
 *     inline on 402.
 *   - gap 087-3: cluster map is now wired to real fan-out cluster data
 *     (size = query_count). Inter-cluster edges are not in the contract
 *     yet; the graph renders as nodes-only until a P2 overlay ships.
 *   - gap 087-4: ScoreCard receives the previous run's scores (history[1])
 *     so the triptych can show trend arrows.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router';
import { useBrand } from '../../lib/brand-context.js';
import { apiGet } from '../../lib/api.js';
import { SCORE_DESIGN } from '../../lib/score-design.js';
import { ScoreBadge } from '../../components/ScoreBadge.js';
import { EmptyState } from '../../components/EmptyState.js';
import { LazyForceGraph } from '../../components/LazyForceGraph.js';
import { StartAnalysisDialog } from '../../components/coverage/StartAnalysisDialog.js';
import { listFanoutClusters, type FanoutClusterRow } from '../../api/fanout.js';
import { useToast } from '../../hooks/useToast.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import {
  StallDiagnostics,
  type StallDiagnosticsJob,
} from '../../components/StallDiagnostics.js';
import { Icon, Spinner } from '../../components/ui/Icon.js';
import { Button } from '../../components/ui/Button.js';
import { Region, RegionHead } from '../../components/ui/Region.js';
import { StatRow, Stat } from '../../components/ui/StatRow.js';
import { resetCoverageData } from '../../api/sitemapAnalyses.js';
import { isCoverageStalled } from '../../lib/coverageStaleness.js';
import { useResolvedTokenColor } from '../../lib/resolve-token-color.js';

/** Phases/statuses that represent a finished run (real scores expected). */
const TERMINAL = ['completed', 'failed', 'cancelled'];

/** A run is stalled/in-flight when its phase is not one of the terminal set. */
function isTerminalPhase(phase: string | null | undefined): boolean {
  return phase != null && TERMINAL.includes(phase);
}

interface CoverageCurrentResponse {
  data: {
    id: string;
    phase: string;
    status: string;
    coverage_score: number | null;
    quality_score: number | null;
    topic_distribution_score: number | null;
    urls_evaluated: number;
    total_urls_in_sitemap: number;
    started_at: string;
    completed_at: string | null;
    scores: {
      coverage_score: number | null;
      quality_score: number | null;
      topic_distribution_score: number | null;
      banner: string | null;
    };
    /** Spec 145 — additive stall-honesty fields (same shape as the detail endpoint). */
    error?: string | null;
    job?: StallDiagnosticsJob | null;
    /** Spec 209 — reason a completed run stopped before covering every cluster. */
    partial_completion_reason?: string | null;
  };
}

interface HistoryResponse {
  data: Array<{
    id: string;
    phase: string;
    status: string;
    coverage_score: number | null;
    quality_score: number | null;
    topic_distribution_score: number | null;
    total_urls_in_sitemap: number;
    urls_evaluated: number;
    started_at: string;
    completed_at: string | null;
    /** Spec 149 — orchestrator job's last-activity time; drives real staleness. */
    job_updated_at?: string | null;
  }>;
}

export function CoverageDashboardPage() {
  const { selectedBrand, loading: brandsLoading } = useBrand();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const brandId = selectedBrand?.id ?? null;

  const [current, setCurrent] = useState<CoverageCurrentResponse['data'] | null>(null);
  const [history, setHistory] = useState<HistoryResponse['data']>([]);
  const [clusters, setClusters] = useState<FanoutClusterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCurrent, setHasCurrent] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const hist = await apiGet<HistoryResponse>(
        `/api/v1/brands/${brandId}/sitemap-analyses`,
      );
      setHistory(hist.data);
      try {
        const cur = await apiGet<CoverageCurrentResponse>(
          `/api/v1/brands/${brandId}/coverage/current`,
        );
        setCurrent(cur.data);
        setHasCurrent(true);
      } catch {
        setHasCurrent(false);
        setCurrent(null);
      }
      try {
        const cs = await listFanoutClusters(brandId);
        setClusters(cs);
      } catch {
        setClusters([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coverage');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onResetCoverage = useCallback(async () => {
    if (!selectedBrand || resetting) return;
    const ok = window.confirm(
      `Permanently delete ALL coverage analyses and page evaluations for "${selectedBrand.name}"?\n\n` +
        `• Sitemap analyses (and per-URL triage, scores, recommendations)\n` +
        `• Page evaluations (and rule results, snapshots, embeddings)\n\n` +
        `Brand config, the rule library, and existing playbook actions are kept.\n\n` +
        `This cannot be undone.`,
    );
    if (!ok) return;
    setResetting(true);
    try {
      const res = await resetCoverageData(selectedBrand.id);
      addToast(
        `Reset complete — ${res.deleted.sitemap_analyses} analyses, ${res.deleted.page_evaluations} evaluations deleted`,
        'success',
      );
      await load();
    } catch (err) {
      addToast(err instanceof Error ? err.message : String(err), 'error');
    } finally {
      setResetting(false);
    }
  }, [selectedBrand, resetting, addToast, load]);

  // react-force-graph-2d paints node.color straight onto Canvas2D's fillStyle,
  // which knows nothing about CSS custom properties — a var() string here
  // would silently fail to paint. Resolve the accent token to a concrete
  // colour in JS instead (spec 228), and re-resolve on theme change.
  const nodeColor = useResolvedTokenColor('--hub-color-action');

  /**
   * The LazyForceGraph expects `{nodes, links}` from react-force-graph-2d.
   * We synthesize nodes from clusters sized by query_count. Edges are
   * intentionally empty — the `/clusters` endpoint does NOT expose inter-
   * cluster similarity, and faking edges would be visually misleading.
   * A P2 overlay (covered vs uncovered) will come once per-cluster
   * coverage scores exist on the API.
   */
  const clusterGraph = useMemo(() => {
    return {
      nodes: clusters.map((c) => ({
        id: c.slug,
        name: c.semantic_label,
        val: Math.max(1, c.query_count),
        // Uniform accent node color to match the Fan-Out cluster map. Without
        // this the force-graph default renders blue, which made the two
        // identical maps look inconsistent.
        color: nodeColor,
      })),
      links: [] as unknown[],
    };
  }, [clusters, nodeColor]);

  // Phase 9 Session E.3 (gap 087-4 tail): the simple `history[1]` lookup
  // landed on a previous analysis whose scores were null (failed/cancelled
  // runs are common on test brands). Skip those — find the most recent prior
  // analysis (i.e. not the current one) where ALL THREE scores are non-null.
  // Without this, ScoreBadge sees `previousValue === null`, computes delta=0,
  // and renders no trend pill — which made the feature appear broken even
  // though the plumbing is correct.
  const prior = useMemo(() => {
    return (
      history.find(
        (h) =>
          h.id !== current?.id &&
          h.coverage_score !== null &&
          h.quality_score !== null &&
          h.topic_distribution_score !== null,
      ) ?? null
    );
  }, [history, current?.id]);

  // Spec 145 — stall honesty. A run is "terminal" when its phase is one of
  // completed/failed/cancelled; otherwise it's in-flight (and, if the cron
  // isn't firing, silently wedged). We show the StallDiagnostics banner when
  // the current analysis is non-terminal OR is a terminal failure carrying an
  // error — the same honesty the detail page already gives. A genuinely
  // completed run with real scores shows NO banner.
  const currentTerminal = isTerminalPhase(current?.phase);
  // Spec 209 — a run that completed with partial coverage carries its reason
  // in `partial_completion_reason` (error stays NULL), so the banner must open
  // for that case too or the truncation would never be shown.
  const showStallBanner =
    current != null &&
    (!currentTerminal ||
      (currentTerminal &&
        ((current.error ?? null) !== null ||
          (current.partial_completion_reason ?? null) !== null)));
  // When stalled, the triptych must NOT imply a real zero/blank score.
  const scoresPending = current != null && !currentTerminal;

  // Phase 9 Session E.2 (gap XC-R2-1): show a spinner while the brand list
  // is still resolving so the page doesn't flash "No brand selected" on
  // every navigation. The provider already exposes `loading` — consumers
  // just weren't reading it.
  if (brandsLoading) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-slate-500"
        data-testid="brands-loading"
      >
        <Spinner className="text-violet-600" />
        <p className="mt-3 text-sm">Loading brands…</p>
      </div>
    );
  }

  if (!selectedBrand) {
    return (
      <div>
        <EmptyState
          title="No brand selected"
          description="Select a brand to view coverage analysis."
        />
      </div>
    );
  }

  const handleCreated = (analysisId: string) => {
    setDialogOpen(false);
    addToast('Coverage analysis started.', 'success');
    navigate(`/coverage/${analysisId}`);
  };
  const handleFlagDisabled = (flag: string) => {
    setDialogOpen(false);
    addToast(
      `Sitemap analysis feature is disabled. Ask an admin to enable \`${flag}\`.`,
      'error',
    );
  };

  return (
    <div data-testid="coverage-dashboard" className="hub-page--dashboard" data-density="compact">
      <Region>
        <Breadcrumbs items={[{ label: 'Coverage' }]} />
        <div className="hub-page__head mt-3">
          <div>
            <h1 className="hub-page__title">Coverage</h1>
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">
              Sitemap-wide content coverage analysis for {selectedBrand.name}.
            </p>
          </div>
          {hasCurrent && current && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setDialogOpen(true)} data-testid="start-analysis-button">
                <Icon name="bx-undo" />
                Run new analysis
              </Button>
              <Button
                variant="danger"
                onClick={onResetCoverage}
                disabled={resetting}
                data-testid="reset-coverage-button"
                title="Permanently delete all coverage analyses and page evaluations for this brand"
              >
                <Icon name="bx-trash" />
                {resetting ? 'Resetting…' : 'Reset coverage data'}
              </Button>
            </div>
          )}
        </div>

        {loading && (
          <div className="mt-4 flex items-center gap-2 text-slate-500">
            <Spinner  />
            Loading analyses…
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
      </Region>

      {/* No analyses: empty state + primary CTA */}
      {!loading && !hasCurrent && (
        <Region>
          <div className="flex flex-col items-center gap-4 py-10 px-6 text-center">
            <EmptyState
              title="No coverage analysis yet"
              description="Run a sitemap coverage analysis to see scores and recommendations."
            />
            <Button variant="primary" onClick={() => setDialogOpen(true)} data-testid="start-analysis-button">
              <Icon name="bx-play" />
              Start analysis
            </Button>
          </div>
        </Region>
      )}

      {/* Three-score triptych */}
      {current && (
        <>
          <Region>
            {/* Spec 145 — stall-honesty banner. Rendered above the triptych
                whenever the current run is in-flight (cron may be wedged) or a
                terminal failure carrying an error, so the scores aren't read as
                a silent real result. */}
            {showStallBanner && (
              <div className="mb-4" data-testid="coverage-stall-banner">
                <StallDiagnostics
                  analysis={{
                    phase: current.phase,
                    error: current.error ?? null,
                    partial_completion_reason:
                      current.partial_completion_reason ?? null,
                    job: current.job ?? null,
                  }}
                  isTerminal={currentTerminal}
                />
              </div>
            )}

            {scoresPending ? (
              /* Honest "pending" treatment — no fake numbers. The analysis
                 hasn't finished, so we say so instead of showing "—" that
                 reads like a real zero/blank score. Still a StatRow — one
                 instrument, whether the news is a number or "Pending". */
              <div data-testid="score-card-triptych-pending">
                <RegionHead title="Content coverage scores" caption={`In progress — phase: ${current.phase}`} />
                <StatRow>
                  {([SCORE_DESIGN.CoverageScore, SCORE_DESIGN.QualityScore, SCORE_DESIGN.TopicDistributionScore]).map((design) => (
                    <Stat
                      key={design.label}
                      label={design.label}
                      value={
                        <span className="hub-chip">
                          <Icon name="bx-time-five" />
                          Pending
                        </span>
                      }
                    />
                  ))}
                </StatRow>
                <p className="mt-3 text-xs text-slate-500">
                  Scores pending — this analysis hasn’t finished yet.
                </p>
              </div>
            ) : (
              <div data-testid="score-card-triptych">
                <RegionHead
                  title="Content coverage scores"
                  caption={
                    current.phase === 'completed'
                      ? `Based on ${current.urls_evaluated} evaluated pages of ${current.total_urls_in_sitemap} in sitemap`
                      : `In progress — phase: ${current.phase}`
                  }
                />
                <StatRow>
                  <Stat
                    label={SCORE_DESIGN.CoverageScore.label}
                    value={
                      <ScoreBadge
                        scoreType="CoverageScore"
                        value={current.scores.coverage_score ?? null}
                        previousValue={prior ? prior.coverage_score : null}
                        size="lg"
                      />
                    }
                  />
                  <Stat
                    label={SCORE_DESIGN.QualityScore.label}
                    value={
                      <ScoreBadge
                        scoreType="QualityScore"
                        value={current.scores.quality_score ?? null}
                        previousValue={prior ? prior.quality_score : null}
                        size="lg"
                      />
                    }
                  />
                  <Stat
                    label={SCORE_DESIGN.TopicDistributionScore.label}
                    value={
                      <ScoreBadge
                        scoreType="TopicDistributionScore"
                        value={current.scores.topic_distribution_score ?? null}
                        previousValue={prior ? prior.topic_distribution_score : null}
                        size="lg"
                      />
                    }
                  />
                </StatRow>
              </div>
            )}
          </Region>

          {/* No-fanout-graph banner */}
          {current.scores.banner === 'no_fanout_graph' && (
            <Region>
              <div
                data-testid="no-fanout-graph-banner"
                className="rounded-lg border border-amber-200 bg-amber-50 p-4"
              >
                <div className="flex items-start gap-2">
                  <Icon name="bx-info-circle" className="mt-0.5 flex-shrink-0 text-amber-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900">
                      No fan-out graph available yet
                    </h3>
                    <p className="mt-1 text-sm text-amber-800">
                      Run fan-out synthesis on this brand first so coverage scoring has clusters to evaluate against.
                    </p>
                  </div>
                </div>
              </div>
            </Region>
          )}

          {/* Cluster map (lazy-loaded force-graph chunk). Hidden when no
              clusters — avoids a visually-empty canvas that confuses users. */}
          {clusters.length > 0 && (
            <Region data-testid="cluster-map-container">
              <header className="mb-3" data-testid="coverage-map-overlay-pending">
                <h2 className="text-sm font-semibold text-slate-900">Cluster map (demand view) — coverage overlay coming soon</h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {clusters.length} fan-out cluster
                  {clusters.length === 1 ? '' : 's'} sized by query count — this is the
                  same demand map as Fan-out.
                </p>
                <p className="mt-0.5 text-xs text-amber-700">
                  The supply overlay (which clusters your site covers vs. leaves
                  uncovered) isn’t wired up here yet, so this view doesn’t yet
                  show coverage — it’s the demand picture for now.
                </p>
              </header>
              <LazyForceGraph
                graphData={clusterGraph}
                width={640}
                height={320}
                nodeLabel="name"
              />
            </Region>
          )}

          {/* Link to detail */}
          <Region>
            <div className="flex items-center justify-end">
              <Link
                to={`/coverage/${current.id}`}
                className="hub-btn hub-btn--ghost"
                data-testid="view-analysis-detail"
              >
                <Icon name="bx-play-circle" />
                View analysis detail
              </Link>
            </div>
          </Region>
        </>
      )}

      {/* History */}
      {history.length > 0 && (
        <Region data-testid="analysis-history">
          <RegionHead title="Analysis history" />
          <div className="overflow-x-auto">
            <table className="hub-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Phase</th>
                  <th className="is-num">Coverage</th>
                  <th className="is-num">Quality</th>
                  <th className="is-num">Topic distribution</th>
                  <th className="is-num">URLs</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const terminal = isTerminalPhase(h.phase);
                  /* Spec 149 — only a job that's been quiet past the stale
                     window is genuinely stalled; a freshly-advanced non-terminal
                     run is honestly "in progress", not alarming. */
                  const rowStalled = isCoverageStalled(h.phase, h.job_updated_at);
                  return (
                    <tr key={h.id}>
                      <td className="text-[var(--hub-color-ink-muted)]">
                        <Link to={`/coverage/${h.id}`} className="text-[var(--hub-color-action-text)]">
                          {new Date(h.started_at).toLocaleString()}
                        </Link>
                      </td>
                      <td className="text-[var(--hub-color-ink-muted)]">{h.phase}</td>
                      {rowStalled ? (
                        /* Spec 145/149 — genuinely stalled run: one amber "Stalled
                           at <phase>" chip across the score columns instead of
                           three bare "—" that look like real zeros/blanks. */
                        <td
                          className="is-num"
                          colSpan={3}
                          data-testid="history-stalled-chip"
                        >
                          <span className="hub-chip hub-chip--warn">
                            Stalled at {h.phase}
                          </span>
                        </td>
                      ) : !terminal ? (
                        /* Spec 149 — non-terminal but recently advanced: honest,
                           non-alarming "In progress · <phase>" chip. */
                        <td
                          className="is-num"
                          colSpan={3}
                          data-testid="history-inprogress-chip"
                        >
                          <span className="hub-chip hub-chip--info">
                            In progress · {h.phase}
                          </span>
                        </td>
                      ) : (
                        <>
                          <td className="is-num">
                            {h.coverage_score === null ? '—' : h.coverage_score.toFixed(0)}
                          </td>
                          <td className="is-num">
                            {h.quality_score === null ? '—' : h.quality_score.toFixed(0)}
                          </td>
                          <td className="is-num">
                            {h.topic_distribution_score === null
                              ? '—'
                              : h.topic_distribution_score.toFixed(0)}
                          </td>
                        </>
                      )}
                      <td className="is-num text-[var(--hub-color-ink-muted)]">
                        {h.urls_evaluated}/{h.total_urls_in_sitemap}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Region>
      )}

      {dialogOpen && selectedBrand && (
        <StartAnalysisDialog
          brand={selectedBrand}
          onCancel={() => setDialogOpen(false)}
          onCreated={handleCreated}
          onFlagDisabled={handleFlagDisabled}
        />
      )}
    </div>
  );
}
